import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export interface AuthenticatedUser {
  id: number;
  username: string;
  email: string;
  is_staff: boolean;
}

interface NavigationDrawerProps {
  user: AuthenticatedUser;
  onLogout: () => void;
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  to: string;
  adminOnly?: boolean;
}

export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({ user, onLogout, children }) => {
  const location = useLocation();

  const navItems = useMemo<NavItem[]>(
    () => [
      { label: 'Home', to: '/welcome' },
      { label: 'Karma', to: '/karma' },
      { label: 'Checklist', to: '/checklist' },
      { label: 'Calendar', to: '/calendar' },
      { label: 'Intake', to: '/intake' },
      { label: 'Messages', to: '/messages' },
      { label: 'Activity Logs', to: '/admin/logs', adminOnly: true },
    ],
    []
  );

  const isAdmin = user.is_staff || user.username.toLowerCase() === 'admin';
  const filteredItems = navItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="folder-shell min-h-screen bg-background text-foreground">
      <aside className="folder-rail">
        <div className="folder-brand">Kitten Connect</div>
        <nav className="folder-tabs" aria-label="Primary navigation">
          {filteredItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn('folder-tab', isActive && 'folder-tab-active')}
            >
              <span className="folder-tab-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="folder-user">{user.username}</div>
        <Button variant="outline" size="sm" onClick={onLogout} className="folder-logout">
          Logout
        </Button>
      </aside>

      <main className="folder-main" key={location.pathname}>
        {children}
      </main>
    </div>
  );
};
